import { useId, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useT } from '../../i18n';
import { errorMessage } from '../../i18n/errorMessage';
import type { MessageKey } from '../../i18n/catalogs';
import {
  MAX_IMPORT_ROWS,
  importMembersChunked,
  parseMemberList,
  previewMemberImport,
  type MemberImportOutcomeEnvelope,
  type MemberImportResult,
  type MemberImportResultRow,
  type MemberImportRow,
} from '../../lib/api/memberImport';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «LAST OPP MEDLEMSLISTE» (owner decision 2A, 2026-10-20).
//
// Three screens, in order, and the middle one is the point:
//
//   1. THE LIST. Pasted, or read from a file the club picks. Parsed in the
//      browser (`parseMemberList`) so the club sees how many people it found
//      before anything leaves the page.
//   2. THE PREVIEW. The server says what EVERY row will do — new member, known
//      player, already a member, invalid — and nothing has happened yet. A
//      club confirms a list it has read, not one it hopes is right. A row the
//      server matched BY NAME says so here, because that is the club asserting
//      «this address is that player», and the assertion should be visible.
//   3. THE RESULT. Per row again: sent, added but not sent, failed. The club
//      can act on the exceptions; the ordinary case is a list of «sendt».
//
// NOTHING IS CLAIMED HERE. The roster row and the invitation are the server's
// (one transaction per row); this screen reports what it was told. Created
// players are handed back up so the player list can show them without a reload.
// ═══════════════════════════════════════════════════════════════════

export interface MemberImportDialogProps {
  onClose: () => void;
  /** Told once the import has run, with the players it created, in the list's shape. */
  onImported: (created: Record<string, unknown>[]) => void;
}

type Step =
  | { kind: 'input' }
  | { kind: 'previewing' }
  | { kind: 'preview'; rows: MemberImportRow[]; result: MemberImportResult }
  | {
      kind: 'importing';
      rows: MemberImportRow[];
      result: MemberImportResult;
      /** Actionable rows carried out so far, of how many (#1139). */
      done: number;
      total: number;
    }
  | { kind: 'done'; result: MemberImportResult };

/** The rows that will change something, which is what the confirm button counts. */
const ACTIONABLE = new Set(['new', 'member_invited', 'invited']);

export function MemberImportDialog({ onClose, onImported }: MemberImportDialogProps) {
  const t = useT();
  const uid = safeId('member-import', useId());
  const ids = { text: `${uid}-text`, file: `${uid}-file`, error: `${uid}-error` };
  const [text, setText] = useState('');
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'input' });
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const rows = parseMemberList(text);
  const tooMany = rows.length > MAX_IMPORT_ROWS;

  /** The server's refusal or outage as a sentence; `null` for a good answer. */
  const problem = (outcome: MemberImportOutcomeEnvelope): string | null => {
    if (outcome.status === 'ok') return null;
    if (outcome.status === 'refused') return errorMessage(t, outcome.reason);
    if (outcome.status === 'protocol') return t('memberImport.protocol');
    return t('memberImport.unreachable');
  };

  const preview = async () => {
    setError(null);
    setStep({ kind: 'previewing' });
    const outcome = await previewMemberImport(rows);
    const message = problem(outcome);
    if (message !== null || outcome.status !== 'ok') {
      setError(message ?? t('memberImport.unreachable'));
      setStep({ kind: 'input' });
      return;
    }
    setStep({ kind: 'preview', rows, result: outcome.result });
  };

  const confirm = async () => {
    if (step.kind !== 'preview') return;
    setError(null);
    const { rows: previewed, result: plan } = step;
    setStep({ kind: 'importing', rows: previewed, result: plan, done: 0, total: 0 });
    // IN CHUNKS (#1139): the server carries out ten rows per request so each
    // request ends inside the platform's window; progress is shown as it goes.
    const outcome = await importMembersChunked(previewed, plan, (done, total) =>
      setStep({ kind: 'importing', rows: previewed, result: plan, done, total }),
    );
    if (outcome.status === 'stopped') {
      // Says how far it got. Confirming again is safe: a row already carried out
      // comes back `pending` or `already_member` and is not sent twice.
      const why = problem(outcome.cause) ?? t('memberImport.unreachable');
      setError(
        outcome.done > 0
          ? `${why} ${t('memberImport.stoppedPartway', { done: outcome.done, total: outcome.total })}`
          : why,
      );
      setStep({ kind: 'preview', rows: previewed, result: plan });
      return;
    }
    onImported(
      outcome.result.rows
        .map((r) => r.created)
        .filter((c): c is Record<string, unknown> => c !== undefined),
    );
    setStep({ kind: 'done', result: outcome.result });
  };

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    const content = await file.text();
    setText(content);
    setFileNote(
      t('memberImport.fileRead', {
        count: parseMemberList(content).length,
        name: file.name,
      }),
    );
  };

  const actionable =
    step.kind === 'preview' || step.kind === 'importing'
      ? step.result.rows.filter((r) => ACTIONABLE.has(r.outcome)).length
      : 0;

  return (
    <Dialog
      heading={t('memberImport.title')}
      onClose={onClose}
      // A stray click outside must not throw away a pasted list.
      closeOnBackdropClick={false}
      closeLabel={t('memberImport.close')}
      className="card p-5 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      describedBy={error ? ids.error : undefined}
    >
      {(step.kind === 'input' || step.kind === 'previewing') && (
        <form
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (rows.length > 0 && !tooMany) void preview();
          }}
        >
          <p className="text-sm text-ink-2">{t('memberImport.lead')}</p>
          <label htmlFor={ids.text} className="block text-[11px] text-ink-3 mt-3">
            {t('memberImport.pasteLabel')}
          </label>
          <textarea
            id={ids.text}
            className="input mt-1 w-full font-mono text-xs"
            rows={8}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setFileNote(null);
            }}
            placeholder={t('memberImport.pastePlaceholder')}
            data-testid="member-import-text"
          />
          <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                id={ids.file}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                className="sr-only"
                // NOT A SECOND, UNNAMED TAB STOP (#1144, WCAG 4.1.2). The
                // visible button beside it opens this picker and carries the
                // name; the input itself is only the mechanism.
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => void readFile(e.target.files?.[0])}
                data-testid="member-import-file"
              />
              <button
                type="button"
                className="btn-outline text-xs"
                onClick={() => fileRef.current?.click()}
              >
                <Upload size={12} aria-hidden="true" /> {t('memberImport.file')}
              </button>
              {fileNote && <span className="text-[11px] text-ink-3">{fileNote}</span>}
            </div>
            <span className="text-[11px] text-ink-3" data-testid="member-import-count">
              {text.trim() === ''
                ? ''
                : rows.length === 0
                  ? t('memberImport.empty')
                  : tooMany
                    ? t('memberImport.tooMany', { max: MAX_IMPORT_ROWS })
                    : t('memberImport.rowsFound', { count: rows.length })}
            </span>
          </div>
          {error && (
            <p id={ids.error} role="alert" className="text-sm text-rose-700 mt-3">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="btn-outline text-xs">
              {t('memberImport.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary text-xs"
              disabled={rows.length === 0 || tooMany || step.kind === 'previewing'}
              data-testid="member-import-preview"
            >
              {step.kind === 'previewing'
                ? t('memberImport.previewing')
                : t('memberImport.preview')}
            </button>
          </div>
        </form>
      )}

      {(step.kind === 'preview' || step.kind === 'importing') && (
        <div className="mt-3">
          <p className="text-sm text-ink-2">{t('memberImport.previewLead')}</p>
          <RowsTable rows={step.result.rows} phase="preview" />
          {error && (
            <p id={ids.error} role="alert" className="text-sm text-rose-700 mt-3">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between gap-2 mt-4 flex-wrap">
            <button
              type="button"
              onClick={() => setStep({ kind: 'input' })}
              className="btn-outline text-xs"
              disabled={step.kind === 'importing'}
            >
              {t('memberImport.back')}
            </button>
            {actionable === 0 ? (
              <span className="text-sm text-ink-3">{t('memberImport.confirmNothing')}</span>
            ) : (
              <button
                type="button"
                onClick={() => void confirm()}
                className="btn-primary text-xs"
                disabled={step.kind === 'importing'}
                data-testid="member-import-confirm"
              >
                {step.kind === 'importing'
                  ? step.total > 0
                    ? t('memberImport.confirmingProgress', { done: step.done, total: step.total })
                    : t('memberImport.confirming')
                  : t('memberImport.confirm', { count: actionable })}
              </button>
            )}
          </div>
        </div>
      )}

      {step.kind === 'done' && (
        <div className="mt-3" role="status" data-testid="member-import-done">
          <p className="text-sm font-medium">
            {t('memberImport.doneTitle', {
              // A row that failed answers `failed`, not `new`, so this counts
              // exactly the players that now exist.
              added: step.result.rows.filter((r) => r.outcome === 'new').length,
              sent: step.result.rows.filter((r) => r.sent === true).length,
            })}
          </p>
          <p className="text-[12px] text-ink-3 mt-1">{t('memberImport.doneHint')}</p>
          <RowsTable rows={step.result.rows} phase="done" />
          <div className="flex justify-end mt-4">
            <button type="button" onClick={onClose} className="btn-primary text-xs">
              {t('memberImport.close')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** The per-row table, before (what will happen) and after (what did). */
function RowsTable({ rows, phase }: { rows: MemberImportResultRow[]; phase: 'preview' | 'done' }) {
  const t = useT();
  return (
    <div className="card overflow-x-auto mt-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-bg-soft text-[11px] font-mono font-semibold text-ink-2 uppercase tracking-wider">
            <th className="text-left px-3 py-2">{t('memberImport.colName')}</th>
            <th className="text-left px-3 py-2">{t('memberImport.colEmail')}</th>
            <th className="text-left px-3 py-2">{t('memberImport.colOutcome')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.index} className="border-t border-line-soft" data-outcome={r.outcome}>
              <td className="px-3 py-1.5 whitespace-nowrap">{r.name || '–'}</td>
              <td className="px-3 py-1.5 whitespace-nowrap font-mono text-xs">{r.email || '–'}</td>
              <td className="px-3 py-1.5">{describe(t, r, phase)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One row's outcome as a sentence, in the club's language. */
function describe(
  t: ReturnType<typeof useT>,
  r: MemberImportResultRow,
  phase: 'preview' | 'done',
): string {
  if (r.outcome === 'invalid') {
    const key = `memberImport.plan.invalid.${r.reason ?? ''}` as MessageKey;
    return KNOWN_INVALID.has(r.reason ?? '') ? t(key) : t('memberImport.plan.invalid');
  }
  if (r.outcome === 'failed') return t('memberImport.done.failed');
  if (phase === 'done' && typeof r.sent === 'boolean') {
    return r.sent ? t('memberImport.done.sent') : t('memberImport.done.notSent');
  }
  const base = t(`memberImport.plan.${r.outcome}` as MessageKey);
  if (r.player && r.matchedBy === 'name') {
    return `${base} · ${t('memberImport.matchedByName', { name: r.player.name })}`;
  }
  if (r.player && r.matchedBy === 'email' && r.player.name !== r.name) {
    return `${base} · ${t('memberImport.matchedByEmail', { name: r.player.name })}`;
  }
  return base;
}

const KNOWN_INVALID = new Set(['name_required', 'email_invalid', 'duplicate_in_upload']);
